import React from "react";
import {
	Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow,
	TablePagination, TextField, MenuItem, Select, Chip, Tabs, Tab, IconButton, Button,
} from "@mui/material";
import {
	AreaChart, Area, BarChart, Bar, LineChart, Line,
	PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
	ResponsiveContainer, CartesianGrid
} from "recharts";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { Helmet } from "react-helmet";
import AdminPageHeader, { PageSpinner } from "../../../components/AdminPageHeader";
import { loadUserStats, useAdminData, userStatsKey } from "../../../services/adminDataStore";

// ───────────────────────── Styles ─────────────────────────
const CHART_STYLE = {
	backgroundColor: "#FFFFFF",
	borderRadius: "20px",
	border: "1px solid #EEF0F5",
	boxShadow: "0 2px 8px rgba(17,19,26,0.04)",
};
const AXIS_STYLE = { fill: "#9CA3AF", fontSize: 12, fontFamily: "inherit" };
const TOOLTIP_STYLE = {
	backgroundColor: "#FFFFFF",
	border: "1px solid #EEF0F5",
	borderRadius: 12,
	color: "#12131A",
	boxShadow: "0 8px 24px rgba(17,19,26,0.12)",
};
const CARD_TITLE = { color: "#12131A", fontWeight: 700, fontSize: 17, mb: 0.5 };
const CARD_SUB = {
	color: "#8B93A1",
	display: "flex",
	alignItems: "center",
	gap: 0.75,
	fontWeight: 400,
	fontSize: 13,
	mb: 1.5,
};
const TABLE_HEAD_CELL = { color: "#8B93A1", borderColor: "#EEF0F5", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };
const TABLE_BODY_CELL = { color: "#1F2430", borderColor: "#EEF0F5", fontSize: 13, whiteSpace: "nowrap" };



const SIGNUP_TREND = Array.from({ length: 30 }, (_, i) => ({
	day: `${i + 1}`,
	signups: Math.round(20 + Math.sin(i / 3) * 10 + i * 0.8 + Math.random() * 8),
}));

const COHORT_DATA = [
	{ cohort: "Jun W1", w0: 100, w1: 62, w2: 41, w3: 33, w4: 28 },
	{ cohort: "Jun W2", w0: 100, w1: 58, w2: 39, w3: 31, w4: null },
	{ cohort: "Jun W3", w0: 100, w1: 65, w2: 44, w3: null, w4: null },
	{ cohort: "Jun W4", w0: 100, w1: 60, w2: null, w3: null, w4: null },
];

const MODALITY_DATA = [
	{ name: "Chat", value: 60 },
	{ name: "Voice", value: 25 },
	{ name: "Video", value: 15 },
];
const PIE_COLORS = ["#60a5fa", "#4ade80", "#fbbf24"];

const MRR_TREND = Array.from({ length: 12 }, (_, i) => ({
	month: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][i],
	mrr: Math.round(20000 + i * 2300 + Math.random() * 1500),
}));

const CHURN_DATA = [
	{ status: "Cancelled", count: 12 },
	{ status: "Expired", count: 8 },
	{ status: "Past due", count: 5 },
	{ status: "Failed pay", count: 7 },
];
const TRIAL_CAP_USERS = [
	{ id: "user_1042", name: "Alex Chen", usage: "98%", plan: "Trial" },
	{ id: "user_2210", name: "Priya Shah", usage: "100%", plan: "Trial" },
	{ id: "user_3387", name: "Jordan Lee", usage: "92%", plan: "Trial" },
	{ id: "user_4456", name: "Sam Park", usage: "97%", plan: "Trial" },
];

const ABANDON_DATA = [
	{ mode: "Voice", rate: 4 },
	{ mode: "Video", rate: 9 },
];

const SESSION_DATA = [
	{ metric: "Avg", minutes: 6.2 },
	{ metric: "p90", minutes: 18 },
];

const RECENT_FEEDBACK = [
	{ user: "user_1842", rating: 2, comment: "Video lag made it unusable last night, kept freezing." },
	{ user: "user_2091", rating: 3, comment: "Voice quality dropped mid-call, had to restart twice." },
];

const AT_RISK_USERS = [
	{ user: "user_1842", lastActive: "Jun 7", daysIdle: 4 },
	{ user: "user_2091", lastActive: "Jun 6", daysIdle: 5 },
	{ user: "user_3310", lastActive: "Jun 6", daysIdle: 5 },
	{ user: "user_4521", lastActive: "Jun 5", daysIdle: 6 },
	{ user: "user_5670", lastActive: "Jun 5", daysIdle: 6 },
];

const CREATOR_LEADERBOARD = [
	{ creator: "luna_ai", fans: 2140, activeFans: 980, msgs: 18400, minutes: 3200, subs: 312, mrr: 8200, conv: "14%" },
	{ creator: "echo_bot", fans: 1820, activeFans: 740, msgs: 15100, minutes: 2700, subs: 210, mrr: 6100, conv: "11%" },
	{ creator: "nova", fans: 1490, activeFans: 610, msgs: 12300, minutes: 2100, subs: 145, mrr: 5300, conv: "9%" },
	{ creator: "kai_voice", fans: 1120, activeFans: 480, msgs: 9800, minutes: 1900, subs: 98, mrr: 3900, conv: "8%" },
];

const CREATORS_LIST = ["reya", "anslee", "reyav2"];

const ALL_USERS = Array.from({ length: 47 }, (_, i) => ({
	id: `user_${1000 + i}`,
	name: `User ${i + 1}`,
	email: `user${i + 1}@example.com`,
	signupDate: `2026-0${(i % 6) + 1}-${(i % 27) + 1}`,
	plan: i % 5 === 0 ? "Enterprise" : i % 3 === 0 ? "Pro" : "Free",
	status: i % 7 === 0 ? "Past due" : "Active",
	lastActive: `Jun ${(i % 12) + 1}`,
	mrr: i % 5 === 0 ? 199 : i % 3 === 0 ? 29.9 : 0,
	creator: CREATORS_LIST[i % CREATORS_LIST.length],
	messages: 20 + ((i * 13) % 400),
	voiceMinutes: (i * 7) % 180,
	videoMinutes: (i * 5) % 120,
}));

const CONVERSATIONS = [
	{ id: "c1", user: "user_1842", creator: "luna_ai", mode: "chat", time: "8:40 PM" },
	{ id: "c2", user: "user_2091", creator: "echo_bot", mode: "voice", time: "7:12 PM" },
	{ id: "c3", user: "user_3310", creator: "nova", mode: "video", time: "6:55 PM" },
	{ id: "c4", user: "user_4521", creator: "luna_ai", mode: "chat", time: "5:30 PM" },
	{ id: "c5", user: "user_5670", creator: "kai_voice", mode: "voice", time: "4:02 PM" },
];

const TRANSCRIPTS = {
	c1: [
		{ role: "user", mode: "chat", text: "Hey, can you call me tonight?", time: "8:40 PM PT" },
		{ role: "assistant", mode: "voice", text: "Of course! Let's talk later.", time: "8:42 PM PT" },
		{ role: "user", mode: "chat", text: "That was great, thanks", time: "9:01 PM PT" },
	],
	c2: [
		{ role: "user", mode: "voice", text: "Can we go over the report again?", time: "7:10 PM PT" },
		{ role: "assistant", mode: "voice", text: "Sure, starting with Q2 numbers...", time: "7:11 PM PT" },
	],
	c3: [
		{ role: "user", mode: "video", text: "Testing video call quality", time: "6:50 PM PT" },
		{ role: "assistant", mode: "video", text: "Looking good on my end!", time: "6:51 PM PT" },
		{ role: "user", mode: "chat", text: "Connection dropped earlier, sorry", time: "6:55 PM PT" },
	],
	c4: [
		{ role: "user", mode: "chat", text: "What's my subscription status?", time: "5:28 PM PT" },
		{ role: "assistant", mode: "chat", text: "You're on the Pro plan, renews Jul 1.", time: "5:30 PM PT" },
	],
	c5: [
		{ role: "user", mode: "voice", text: "Quick question about billing", time: "4:00 PM PT" },
		{ role: "assistant", mode: "voice", text: "Happy to help, go ahead.", time: "4:02 PM PT" },
	],
};

const CREATOR_STATS = {
	reya: { messages: 18400, voiceMinutes: 3200, videoMinutes: 1100 },
	anslee: { messages: 9800, voiceMinutes: 1900, videoMinutes: 640 },
	reyav2: { messages: 5300, voiceMinutes: 980, videoMinutes: 310 },
};

function formatMinutes(mins) {
	const h = Math.floor(mins / 60);
	const m = mins % 60;
	return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ───────────────────────── Sub-components ─────────────────────────

function KpiCard({ item }) {
	return (
		<Box
			borderRadius="30px"
			p={2.5}
			className="kPiCard"
			sx={{
				background: "#fff",
				border: "1px solid #ECEEF4",
				borderRadius: "24px",
				p: 3,
				height: 180,
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
				transition: "all .2s ease",

				"&:hover": {
					transform: "translateY(-2px)",
					boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
				},
			}}
		>
			<Typography fontSize={34} color="#12131A" fontWeight={800} className="title" letterSpacing="-0.5px" lineHeight={1.1}>
				{item.value}
				{/* {item.delta && (
					<Typography
						component="span"
						fontSize={12}
						ml={1}
						color={item.deltaPositive ? "#16A34A" : "#DC2626"}
					>
						{item.delta}
					</Typography>
				)} */}
			</Typography>
			<Typography fontSize={13.5} mt={0.75}>{item.label}</Typography>
			{item.sub && <Typography fontSize={12} color="#9CA3AF" mt={0.75}>{item.sub}</Typography>}
		</Box>
	);
}

function ChartCard({ title, subtitle, children, height = 220, gridProps }) {
	return (
		<Grid item {...gridProps}>
			<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
				<Typography sx={CARD_TITLE}>{title}</Typography>
				{subtitle && <Typography sx={CARD_SUB}>{subtitle}</Typography>}
				<ResponsiveContainer width="100%" height={height}>
					{children}
				</ResponsiveContainer>
			</Box>
		</Grid>
	);
}

function ActivationFunnel({ data }) {
	console.log(data);
	return (
		<Grid item xs={12} md={4}>
			<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
				<Typography sx={CARD_TITLE}>Activation funnel</Typography>
				<Typography sx={CARD_SUB}>Signup to subscribed</Typography>
				<ResponsiveContainer width="100%" height={220}>
					<BarChart data={data} layout="vertical" margin={{ left: 20 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" horizontal={false} />
						<XAxis type="number" tick={AXIS_STYLE} axisLine={false} tickLine={false} domain={[0, 100]} unit="" />
						<YAxis type="category" dataKey="stage" tick={AXIS_STYLE} axisLine={false} tickLine={false} width={90} />
						<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}`, ""]} />
						<Bar dataKey="value" fill="#FF4DA6" radius={[0, 4, 4, 0]} barSize={18} />
					</BarChart>
				</ResponsiveContainer>
			</Box>
		</Grid>
	);
}

function SignupTrend({ data }) {
	return (
		<ChartCard title="Signup trend" subtitle="Daily, last 30 days" gridProps={{ xs: 12, md: 4 }}>
			<AreaChart data={data}>
				<defs>
					<linearGradient id="gradSignup" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#FF4DA6" stopOpacity={0.35} />
						<stop offset="95%" stopColor="#FF4DA6" stopOpacity={0} />
					</linearGradient>
				</defs>
				<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
				<XAxis dataKey="day" tick={AXIS_STYLE} axisLine={false} tickLine={false} interval={4} />
				<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
				<Tooltip contentStyle={TOOLTIP_STYLE} />
				<Area type="monotone" dataKey="signups" stroke="#FF4DA6" strokeWidth={2.5} fill="url(#gradSignup)" />
			</AreaChart>
		</ChartCard>
	);
}

function getCohortColor(value) {
	if (value === null || value === undefined) return "transparent";
	if (value >= 60) return "rgba(74, 222, 128, 0.25)";
	if (value >= 40) return "rgba(251, 191, 36, 0.25)";
	return "rgba(251, 113, 133, 0.25)";
}

function RetentionCohort({ data }) {
	const weeks = ["w0", "w1", "w2", "w3", "w4"];
	return (
		<Grid item xs={12} md={7}>
			<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
				<Typography sx={CARD_TITLE}>Retention cohort</Typography>
				<Typography sx={CARD_SUB}>Weekly cohorts x week offset</Typography>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={TABLE_HEAD_CELL}>Cohort</TableCell>
							{weeks.map((w) => (
								<TableCell key={w} sx={{ ...TABLE_HEAD_CELL, textAlign: "center" }}>
									{w.toUpperCase()}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data?.map((row) => (
							<TableRow key={row.cohort}>
								<TableCell sx={TABLE_BODY_CELL}>{row.cohort}</TableCell>
								{weeks.map((w) => (
									<TableCell
										key={w}
										sx={{ ...TABLE_BODY_CELL, textAlign: "center", backgroundColor: getCohortColor(row[w]) }}
									>
										{row[w] !== null && row[w] !== undefined ? `${row[w]}%` : "\u2014"}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Grid>
	);
}

function ModalityMix({ data }) {
	return (
		<Grid item xs={12} md={4}>
			<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
				<Typography sx={CARD_TITLE}>Modality mix</Typography>
				<Typography sx={CARD_SUB}>Share of chat / voice / video</Typography>
				<ResponsiveContainer width="100%" height={180}>
					<PieChart>
						<Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={4}>
							{data.map((_, i) => (
								<Cell key={i} fill={PIE_COLORS[i]} stroke="#FFFFFF" strokeWidth={3} />
							))}
						</Pie>
						<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}%`, ""]} />
					</PieChart>
				</ResponsiveContainer>
				<Box display="flex" justifyContent="center" gap={2} mt={1}>
					{data.map((d, i) => (
						<Box key={d.name} display="flex" alignItems="center" gap={0.5}>
							<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: PIE_COLORS[i] }} />
							<Typography fontSize={11} color="#6B7280">{d.name} {d.value}%</Typography>
						</Box>
					))}
				</Box>
			</Box>
		</Grid>
	);
}

function MonetizationRow({ data, churnData, trialCapUsers }) {
	return (
		<>
			<ChartCard title="MRR trend" subtitle="Monthly recurring revenue" gridProps={{ xs: 12, md: 4 }}>
				<AreaChart data={data}>
					<defs>
						<linearGradient id="gradMrr" x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor="#FF4DA6" stopOpacity={0.3} />
							<stop offset="95%" stopColor="#FF4DA6" stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
					<XAxis dataKey="month" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
					<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
					<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, "MRR"]} />
					<Area type="monotone" dataKey="mrr" stroke="#FF4DA6" strokeWidth={2.5} fill="url(#gradMrr)" />
				</AreaChart>
			</ChartCard>

			<ChartCard title="Churn & failures" subtitle="Cancelled / expired / past due / failed" gridProps={{ xs: 12, md: 4 }}>
				<ResponsiveContainer width="100%" height={180}>
					<BarChart data={churnData} barCategoryGap="30%" >
						<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
						<XAxis dataKey="status" tick={{ ...AXIS_STYLE, fontSize: 10 }} axisLine={false} tickLine={false} />
						<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
						<Tooltip contentStyle={TOOLTIP_STYLE} />
						<Bar dataKey="count" fill="#fb7185" radius={[4, 4, 0, 0]} />
					</BarChart>
				</ResponsiveContainer>
			</ChartCard>

			<Grid item xs={12} md={4}>
				<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
					<Typography sx={CARD_TITLE}>Trial cap (upsell)</Typography>
					<Typography sx={CARD_SUB}>{trialCapUsers?.length} users near limit</Typography>
					<Box display="flex" flexDirection="column" gap={1}>
						{trialCapUsers?.map((u) => (
							<Box key={u.id} display="flex" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography fontSize={12} color="#1F2430">{u.name}</Typography>
									<Typography fontSize={10} color="#8B93A1">{u.id}</Typography>
								</Box>
								<Chip label={u.usage} size="small" sx={{ backgroundColor: "#fbbf2422", color: "#fbbf24", fontSize: 11 }} />
							</Box>
						))}
					</Box>
				</Box>
			</Grid>
		</>
	);
}

function ReliabilityRow({ abandonCallRate, sessionDuration, feedback, sessionMode, setSessionMode }) {
	const totalRating = feedback?.length > 0 ? feedback?.reduce((sum, item) => sum + item.rating, 0) : 0;
	const averageRating = feedback?.length > 0 ? (totalRating / feedback?.length) : 0;
	return (
		<>
			{/* <Grid item xs={12} md={4}>
				<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
						<Typography sx={CARD_TITLE}>Session duration</Typography>
						<Select
							size="small"
							value={sessionMode}
							onChange={(e) => setSessionMode(e.target.value)}
							sx={{
								color: "#1F2430", backgroundColor: "#F7F8FC", fontSize: 11, height: 28,
								"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" },
							}}
						>
							<MenuItem value="voice">Voice</MenuItem>
							<MenuItem value="video">Video</MenuItem>
						</Select>
					</Box>
					<Typography sx={CARD_SUB}>Avg and p90, minutes</Typography>
					<ResponsiveContainer width="100%" height={180}>
						<BarChart data={sessionDuration} barCategoryGap="40%">
							<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
							<XAxis dataKey="metric" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
							<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} unit="m" />
							<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}m`, ""]} />
							<Bar dataKey="minutes" fill="#60a5fa" radius={[4, 4, 0, 0]} barSize={40} />
						</BarChart>
					</ResponsiveContainer>

					<Box mt={1.5} display="flex" flexDirection="column" gap={0.75}>
						{[
							{ label: 'p90', desc: '90% of completed calls are shorter than this duration' },
							{ label: 'Avg', desc: 'Average call duration in minutes' },
						].map(({ label, desc }) => (
							<Box key={label} display="flex" alignItems="flex-start" gap={1}>
								<Typography sx={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', minWidth: 28, pt: '1px' }}>
									{label}
								</Typography>
								<Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>
									{desc}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Grid> */}
			<ChartCard title="Abandoned call rate" subtitle="Voice vs video" gridProps={{ xs: 12, md: 6 }} height={180}>
				<BarChart data={abandonCallRate} barCategoryGap="40%">
					<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
					<XAxis dataKey="mode" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
					<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} unit="%" />
					<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}%`, "Abandoned"]} />
					<Bar dataKey="rate" fill="#fb7185" radius={[4, 4, 0, 0]} barSize={40} />
				</BarChart>
			</ChartCard>

			<Grid item xs={12} md={6}>
				<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
					<Typography sx={CARD_TITLE}>Feedback</Typography>
					<Typography fontSize={32} color="#12131A" fontWeight={800} mb={1}>
						{averageRating} <Typography component="span" fontSize={14} color="#8B93A1">/ 5 avg</Typography>
					</Typography>
					<Box display="flex" flexDirection="column" gap={1}>
						{feedback?.map((f, i) => (
							<Box key={i} sx={{ borderTop: "1px solid #EEF0F5", pt: 1 }}>
								<Typography fontSize={11} color="#6B7280">{f?.username} · {f?.user_id}</Typography>
								<Typography fontSize={12} color="#3A3F4B">{f?.comment}</Typography>
							</Box>
						))}
					</Box>
				</Box>
			</Grid>
		</>
	);
}

function AtRiskAndLeaderboard({ atRiskUsers, creatorData }) {
	return (
		<>
			<Grid item xs={12} md={5}>
				<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
					<Typography sx={CARD_TITLE}>At-risk users</Typography>
					<Typography sx={CARD_SUB}>Active 7d+, silent 48h</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={TABLE_HEAD_CELL}>User</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Last active</TableCell>
								<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Days idle</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{atRiskUsers?.map((u) => (
								<TableRow key={u.user}>
									<TableCell sx={TABLE_BODY_CELL}>{u.user}</TableCell>
									<TableCell sx={TABLE_BODY_CELL}>{u.lastActive}</TableCell>
									<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right", color: "#fb7185" }}>{u.daysIdle}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Grid>

			<Grid item xs={12} md={7}>
				<Box borderRadius={2} p={2.5} sx={CHART_STYLE} height="100%">
					<Typography sx={CARD_TITLE}>Creator leaderboard</Typography>
					<Typography sx={CARD_SUB}>Ranked by MRR</Typography>
					<Box sx={{ overflowX: "auto" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={TABLE_HEAD_CELL}>Creator</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Fans</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Active fans</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Msgs</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Video</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Video</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Subs</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>MRR</TableCell>
									<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Conv.</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{creatorData?.map((c) => (
									<TableRow key={c.creator}>
										<TableCell sx={TABLE_BODY_CELL}>{c?.creatorname}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.fans}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.active_fans_30d}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.total_fan_msgs}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.video_min}m</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.voice_min}m</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.subs}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>${c?.mrr}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{c?.conversion_pct}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</Box>
			</Grid>
		</>
	);
}

// ───────────────────────── Page header / controls ─────────────────────────

function DashboardHeader({ range, setRange, onRefresh }) {
	return (
		<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
			<Typography variant="h3" color="#12131A" fontSize={26} fontWeight={700}>Dashboard</Typography>
			<Box display="flex" gap={1} alignItems="center">
				<Tabs
					value={range}
					onChange={(_, v) => setRange(v)}
					sx={{
						minHeight: 36,
						"& .MuiTab-root": { color: "#6B7280", minHeight: 36, fontSize: 12, textTransform: "none" },
						"& .Mui-selected": { color: "#12131A !important" },
						"& .MuiTabs-indicator": { backgroundColor: "#fbbf24" },
					}}
				>
					<Tab label="24h" value="24h" />
					<Tab label="7d" value="7d" />
					<Tab label="30d" value="30d" />
					<Tab label="Custom" value="custom" />
				</Tabs>
				<IconButton onClick={onRefresh} size="small" sx={{ color: "#6B7280", border: "1px solid #E2E4EE", borderRadius: 1 }}>
					<FiExternalLink style={{ transform: "rotate(45deg)" }} />
				</IconButton>
			</Box>
		</Box>
	);
}
// ───────────────────────── Main page ─────────────────────────

export default function Dashboard() {
	const [sessionMode, setSessionMode] = React.useState("voice");
	const {
		loaded,
		summary: dashboardSummary,
		funnel: funnelchart,
		signupTrend: dailysignupTrend,
		modalityMix: modalityMixdata,
		mrrTrend,
		churnData,
		trialCapUsers,
		abandonCallRate,
		sessionDuration: sessionDurationByMode,
		feedback,
		atRiskUsers,
		creators: creatorData,
		creatorList: creatorIdList,
	} = useAdminData();

	const sessionDuration = sessionDurationByMode?.[sessionMode] || [];

	const summary = [
		{ label: "New signups 24h", value: dashboardSummary?.signups_24h || 0, delta: "+12%", deltaPositive: true },
		{ label: "New signups 7d", value: dashboardSummary?.signups_7d || 0, delta: "+8%", deltaPositive: true },
		{ label: "New signups 30d", value: dashboardSummary?.signups_30d || 0, delta: "-3%", deltaPositive: false },
		{ label: "Total users", value: dashboardSummary?.total_users || 0 },
		{ label: "Daily Active Users / Weekly Active Users / Monthly Active Users", value: `${dashboardSummary?.dau || 0} / ${dashboardSummary?.wau || 0} / ${dashboardSummary?.mau || 0}`, sub: `Stickiness ${dashboardSummary?.stickiness_pct || 0}% ` },
		{ label: "Activation rate", value: `${dashboardSummary?.activation_rate_1msg_pct || 0}% / ${dashboardSummary?.activation_rate_3msg_pct || 0}%`, sub: ">=1 msg / >=3 msgs" },
		{ label: "Active Paid subscribers", value: dashboardSummary?.active_subs || 0 },
		{ label: "Monthly recurring revenue($)", value: `$ ${dashboardSummary?.mrr || 0}` },
		{ label: "Trial \u2192 paid", value: `${dashboardSummary?.trial_to_paid_pct || 0}%` },
	]
	return (
		<>
			<Helmet>
				<title>Dashboard</title>
			</Helmet>
			<div className="dashboard-main">
				<Box maxWidth={1600} mx="auto">
					<AdminPageHeader
						title="Dashboard"
						subtitle="Engagement, monetization, and reliability at a glance"
					/>
					{!loaded ? <PageSpinner /> : (
						<>
							<div className="sessionDuration-with-widgets">
								<div className="sessionDuration-div">
									<Box borderRadius={2} p={3} sx={CHART_STYLE} className="duration-card">
										<Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
											<Typography sx={{ ...CARD_TITLE, fontSize: 30 }}>Session duration</Typography>
											<Select
												size="small"
												value={sessionMode}
												onChange={(e) => setSessionMode(e.target.value)}
												sx={{
													color: "#1F2430", backgroundColor: "#F7F8FC", fontSize: 11, height: 28,
													"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" },
												}}
											>
												<MenuItem value="voice">Voice</MenuItem>
												<MenuItem value="video">Video</MenuItem>
											</Select>
										</Box>
										<Typography sx={CARD_SUB}>Avg and p90, minutes</Typography>
										<ResponsiveContainer width="100%" height={"65%"}>
											<BarChart data={sessionDuration} barCategoryGap="40%">
												<CartesianGrid strokeDasharray="3 3" stroke="rgba(17,19,26,0.06)" />
												<XAxis dataKey="metric" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
												<YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} unit="m" />
												<Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}m`, ""]} />
												<Bar dataKey="minutes" fill="#FF4DA6" radius={[4, 4, 0, 0]} barSize={40} />
											</BarChart>
										</ResponsiveContainer>

										<Box mt={1.5} display="flex" flexDirection="column" gap={0.75}>
											{[
												{ label: 'p90', desc: '90% of completed calls are shorter than this duration' },
												{ label: 'Avg', desc: 'Average call duration in minutes' },
											].map(({ label, desc }) => (
												<Box key={label} display="flex" alignItems="flex-start" gap={1}>
													<Typography sx={{ fontSize: 11, fontWeight: 700, color: '#FF4DA6', minWidth: 28, pt: '1px' }}>
														{label}
													</Typography>
													<Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>
														{desc}
													</Typography>
												</Box>
											))}
										</Box>
									</Box>
								</div>
								<div className="widgets-div">
									<Grid
										container
										spacing={2}
									>
										{summary.map((item) => (
											<Grid item xs={12}
												sm={6}
												lg={4} key={item.label}>
												<KpiCard item={item} />
											</Grid>
										))}
									</Grid>
								</div>
							</div>
							
							{/* Row B: Activation funnel + Signup trend */}
							<Grid container spacing={2} mt={0}>
								<ActivationFunnel data={funnelchart} />
								<SignupTrend data={dailysignupTrend} />
								<ModalityMix data={modalityMixdata} />

							</Grid>

							{/* Row C: Retention cohort + Modality mix */}
							<Grid container spacing={2} mt={0}>
								{/* <RetentionCohort data={retention} /> */}
								<MonetizationRow data={mrrTrend} churnData={churnData} trialCapUsers={trialCapUsers} />

							</Grid>

							{/* Row D: Monetization */}
							{/* <Grid container spacing={2} mt={0}>
						<MonetizationRow data={mrrTrend} churnData={churnData} trialCapUsers={trialCapUsers} />
					</Grid> */}

							{/* Row E: Reliability */}
							<Grid container spacing={2} mt={0}>
								<ReliabilityRow
									abandonCallRate={abandonCallRate}
									sessionDuration={sessionDuration}
									feedback={feedback}
									sessionMode={sessionMode}
									setSessionMode={setSessionMode}
								/>
							</Grid>

							{/* Row F: At-risk users + Creator leaderboard */}
							<Grid container spacing={2} mt={0}>
								<AtRiskAndLeaderboard atRiskUsers={atRiskUsers} creatorData={creatorData} />
							</Grid>
						</>
					)}
				</Box>
			</div>
		</>
	);
}